/**
 * @fileoverview Input/output schemas for the robust-chapter-formatter-v1 agent.
 *
 * This agent receives a scoped text region containing a single chapter
 * (with some padding around the boundaries) and faithfully reproduces
 * the chapter's text with clean formatting.
 * Designed to run on a cheap model (Gemini 3.1 Flash Lite) at massive scale.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema.extend({
  textInput: z.string().describe('A scoped text region containing the chapter to extract, with some surrounding context from adjacent chapters.'),
  chapterNumber: z.number().describe('The chapter number to extract (1-indexed).'),
  chapterHeading: z.string().describe('The exact chapter heading as it appears in the text (e.g., "Chapter 12", "PROLOGUE").'),
  previousChapterHeading: z.string().optional().describe('The heading of the previous chapter, if any. Helps define the start boundary.'),
  nextChapterHeading: z.string().optional().describe('The heading of the next chapter, if any. Helps define the end boundary.'),
  totalChapters: z.number().describe('The total number of chapters detected in the document.'),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  content: z.string().describe(
    'The faithfully reproduced chapter, formatted as a single HTML string ' +
    'ready for the Tiptap editor. Paragraphs are wrapped in <p> tags, ' +
    'scene breaks are emitted as <hr>, and intentional line breaks use <br>. ' +
    'The exact words from the source are preserved; only whitespace, line ' +
    'breaks, and paragraph structure are normalized.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
